/**
 * LaTeX预处理器
 * 用于在用户消息发送前，自动为裸LaTeX命令添加定界符
 */

/**
 * 支持的LaTeX命令列表
 * 基于虚拟键盘和符号助手的符号
 */
const LATEX_COMMANDS = {
  // 结构命令（需要参数）
  withArgs: [
    'frac',      // 分数
    'sqrt',      // 根号
    'sum',       // 求和
    'int',       // 积分
    'lim',       // 极限
    'prod',      // 连乘
    'binom',     // 二项式
  ],
  
  // 符号命令（无参数）
  symbols: [
    // 希腊字母
    'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'zeta', 'eta', 'theta',
    'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'pi', 'rho',
    'sigma', 'tau', 'upsilon', 'phi', 'chi', 'psi', 'omega',
    'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta',
    'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Pi', 'Rho',
    'Sigma', 'Tau', 'Upsilon', 'Phi', 'Chi', 'Psi', 'Omega',
    
    // 关系符号
    'leq', 'geq', 'neq', 'approx', 'equiv', 'sim', 'cong',
    'propto', 'parallel', 'perp',
    
    // 运算符号
    'pm', 'mp', 'times', 'div', 'cdot', 'ast', 'star',
    'circ', 'bullet', 'oplus', 'ominus', 'otimes',
    
    // 特殊符号
    'infty', 'partial', 'nabla', 'angle', 'triangle',
    'square', 'diamond', 'emptyset', 'forall', 'exists',
    
    // 箭头
    'leftarrow', 'rightarrow', 'leftrightarrow',
    'Leftarrow', 'Rightarrow', 'Leftrightarrow',
    'uparrow', 'downarrow', 'updownarrow',
    
    // 集合符号
    'in', 'notin', 'subset', 'supset', 'subseteq', 'supseteq',
    'cup', 'cap', 'setminus', 'bigcup', 'bigcap',
    
    // 逻辑符号
    'land', 'lor', 'lnot', 'implies', 'iff',
    
    // 其他
    'dots', 'cdots', 'vdots', 'ddots', 'ldots',
  ]
};

/**
 * 检测文本中是否包含裸LaTeX命令
 */
function hasBareLaTeX(text: string): boolean {
  // 检查是否包含反斜杠开头的命令
  const hasCommand = /\\[a-zA-Z]+/.test(text);
  
  // 检查是否包含上下标
  const hasScript = /[a-zA-Z0-9]\^{|[a-zA-Z0-9]_{/.test(text);
  
  return hasCommand || hasScript;
}

/**
 * 预处理用户输入的LaTeX表达式
 * 自动为裸LaTeX命令添加 $ ... $ 定界符
 * 
 * @param text 用户输入的文本
 * @returns 处理后的文本
 */
export function preprocessUserLatex(text: string): string {
  if (!text || typeof text !== 'string') {
    return text || '';
  }

  // 如果没有LaTeX命令，直接返回
  if (!hasBareLaTeX(text)) {
    return text;
  }

  let processed = text;
  
  // 步骤1：保护已有的定界符内容
  const protectedSegments: { placeholder: string; content: string }[] = [];
  let segmentIndex = 0;

  // 保护 $$...$$ 块级公式
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (match) => {
    const placeholder = `__PROTECTED_BLOCK_${segmentIndex}__`;
    protectedSegments.push({ placeholder, content: match });
    segmentIndex++;
    return placeholder;
  });

  // 保护 $...$ 行内公式
  processed = processed.replace(/\$([^\$]+)\$/g, (match) => {
    const placeholder = `__PROTECTED_INLINE_${segmentIndex}__`;
    protectedSegments.push({ placeholder, content: match });
    segmentIndex++;
    return placeholder;
  });

  // 保护 \[...\] 块级公式
  processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (match) => {
    const placeholder = `__PROTECTED_LATEX_BLOCK_${segmentIndex}__`;
    protectedSegments.push({ placeholder, content: match });
    segmentIndex++;
    return placeholder;
  });

  // 保护 \(...\) 行内公式
  processed = processed.replace(/\\\((.*?)\\\)/g, (match) => {
    const placeholder = `__PROTECTED_LATEX_INLINE_${segmentIndex}__`;
    protectedSegments.push({ placeholder, content: match });
    segmentIndex++;
    return placeholder;
  });

  // 步骤2：识别并处理裸LaTeX命令
  
  // 处理连续的LaTeX表达式（可能包含多个命令、上下标等）
  // 匹配模式：LaTeX命令（可能带参数和上下标）或独立的上下标表达式
  // 改进的正则：匹配 \command{arg}_{sub}^{sup} 或 \command_{sub}^{sup} 或 variable_{sub}^{sup}
  const latexPattern = /(?:\\[a-zA-Z]+(?:\[[^\]]*\])?(?:\{[^}]*\})*(?:[\^_]\{[^}]*\})*|[a-zA-Z0-9]+[\^_]\{[^}]*\}(?:[\^_]\{[^}]*\})*)+(?:\s*(?:\\[a-zA-Z]+(?:\[[^\]]*\])?(?:\{[^}]*\})*(?:[\^_]\{[^}]*\})*|[a-zA-Z0-9]+[\^_]\{[^}]*\}(?:[\^_]\{[^}]*\})*|[+\-*/=<>])\s*)*/g;
  
  processed = processed.replace(latexPattern, (match) => {
    // 跳过保护的占位符
    if (match.startsWith('__PROTECTED_')) {
      return match;
    }
    
    // 检查是否包含中文字符（如果包含中文，不处理）
    const hasChinese = /[\u4e00-\u9fa5\u3000-\u303f\uff00-\uffef]/.test(match);
    if (hasChinese) {
      return match;
    }
    
    // 检查是否真的包含LaTeX命令或上下标
    const hasLatexCommand = /\\[a-zA-Z]+/.test(match);
    const hasScript = /[\^_]\{/.test(match);
    
    if (hasLatexCommand || hasScript) {
      // 添加定界符
      return `$${match}$`;
    }
    
    return match;
  });

  // 步骤3：恢复保护的内容
  protectedSegments.forEach(({ placeholder, content }) => {
    processed = processed.replace(placeholder, content);
  });

  return processed;
}

/**
 * 检查文本是否已经有定界符
 * 用于避免重复处理
 */
export function hasDelimiters(text: string): boolean {
  return /\$.*?\$|\\\[[\s\S]*?\\\]|\\\(.*?\\\)/.test(text);
}
